import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import Modal from '../../../components/ui/Modal';

const LoginForm = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [signUpData, setSignUpData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: ''
  });
  const [signUpErrors, setSignUpErrors] = useState({});

  const roleOptions = [
    { value: 'admin', label: 'مدير النظام', description: 'صلاحيات كاملة للنظام' },
    { value: 'property_manager', label: 'مدير الخصائص', description: 'إدارة العقارات والقوائم' },
    { value: 'contract_specialist', label: 'أخصائي العقود', description: 'معالجة العقود والموافقات' },
    { value: 'client_manager', label: 'مدير العملاء', description: 'إدارة علاقات العملاء' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'يرجى إدخال البريد الإلكتروني';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!formData?.password) {
      newErrors.password = 'يرجى إدخال كلمة المرور';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }

    if (!formData?.role) {
      newErrors.role = 'يرجى اختيار الدور الوظيفي';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await signIn(formData?.email, formData?.password);
      onLoginSuccess?.();
      navigate('/');
    } catch (error) {
      setLoginAttempts(prev => prev + 1);
      setErrors({ 
        submit: error?.message || 'فشل تسجيل الدخول. يرجى التحقق من بيانات الاعتماد الخاصة بك.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPasswordModal(true);
    setRecoveryEmail('');
    setRecoveryMessage('');
  };

  const handlePasswordRecovery = async () => {
    if (!recoveryEmail?.trim()) {
      setRecoveryMessage('يرجى إدخال البريد الإلكتروني');
      return;
    }
    if (!/\S+@\S+\.\S+/?.test(recoveryEmail)) {
      setRecoveryMessage('البريد الإلكتروني غير صحيح');
      return;
    }

    try {
      setIsLoading(true);
      // Simulate password recovery email sending
      await new Promise(resolve => setTimeout(resolve, 1500));
      setRecoveryMessage('تم إرسال رابط استعادة كلمة المرور إلى بريدك الإلكتروني');
      setTimeout(() => {
        setShowForgotPasswordModal(false);
      }, 2000);
    } catch (error) {
      setRecoveryMessage('حدث خطأ. يرجى المحاولة مرة أخرى');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async () => {
    const newErrors = {};

    if (!signUpData?.fullName?.trim()) {
      newErrors.fullName = 'يرجى إدخال الاسم الكامل';
    }

    if (!signUpData?.email?.trim()) {
      newErrors.email = 'يرجى إدخال البريد الإلكتروني';
    } else if (!/\S+@\S+\.\S+/?.test(signUpData?.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!signUpData?.password) {
      newErrors.password = 'يرجى إدخال كلمة المرور';
    } else if (signUpData?.password?.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }

    if (signUpData?.password !== signUpData?.confirmPassword) {
      newErrors.confirmPassword = 'كلمات المرور غير متطابقة';
    }

    if (!signUpData?.role) {
      newErrors.role = 'يرجى اختيار الدور الوظيفي';
    }

    setSignUpErrors(newErrors);

    if (Object.keys(newErrors)?.length > 0) {
      return;
    }

    try {
      setIsLoading(true);
      await signUp(signUpData?.email, signUpData?.password, {
        full_name: signUpData?.fullName,
        role: signUpData?.role
      });
      setShowSignUpModal(false);
      setRecoveryMessage('تم إنشاء الحساب بنجاح! حسابك قيد المراجعة من قبل المسؤول. سيتم إخطارك عند الموافقة.');
      
      // Clear sign up form
      setSignUpData({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: ''
      });
    } catch (error) {
      setSignUpErrors({ submit: error?.message || 'فشل إنشاء الحساب' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="w-full space-y-6" noValidate>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            البريد الإلكتروني
          </label>
          <Input
            type="email"
            placeholder="example@realestate.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            className={errors?.email ? 'border-red-500' : ''}
          />
          {errors?.email && (
            <p className="text-red-400 text-sm mt-1">{errors?.email}</p>
          )}
        </div>

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            label="كلمة المرور"
            placeholder="أدخل كلمة المرور"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
            disabled={isLoading}
            className="text-right"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute left-3 top-[38px] text-white/60 hover:text-white transition-colors"
            aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <Select
          label="الدور الوظيفي"
          placeholder="اختر دورك الوظيفي"
          options={roleOptions}
          value={formData?.role}
          onChange={(value) => handleInputChange('role', value)}
          error={errors?.role}
          required
          disabled={isLoading}
        />
      </div>
      <div className="flex items-center justify-between">
        <Checkbox
          label="تذكرني"
          checked={formData?.rememberMe}
          onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
          disabled={isLoading}
        />

        <button
          type="button"
          onClick={handleForgotPassword}
          className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
        >
          نسيت كلمة المرور؟
        </button>
      </div>

      {errors?.submit && (
        <div className="flex items-start gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors?.submit}</p>
        </div>
      )}

      {loginAttempts >= 3 && (
        <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-warning">
            تم تجاوز عدد محاولات تسجيل الدخول. يرجى الانتظار قبل المحاولة مرة أخرى.
          </p>
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={isLoading || loginAttempts >= 5}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>جاري تسجيل الدخول...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Icon name="LogIn" size={20} />
            <span>تسجيل الدخول</span>
          </div>
        )}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setShowSignUpModal(true)}
          className="text-sm text-white/70 hover:text-white transition-colors"
        >
          ليس لديك حساب؟{' '}
          <span className="text-primary hover:text-primary/80 font-medium">
            إنشاء حساب جديد
          </span>
        </button>
      </div>
    </form>

    {/* Forgot Password Modal */}
    <Modal
      isOpen={showForgotPasswordModal}
      onClose={() => setShowForgotPasswordModal(false)}
      title="استعادة كلمة المرور"
      icon="Key"
      iconColor="var(--color-primary)"
      footer={null}
    >
      <div className="space-y-4">
        <p className="text-sm text-white/70">
          أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور
        </p>
        <Input
          type="email"
          label="البريد الإلكتروني"
          placeholder="example@realestate.com"
          value={recoveryEmail}
          onChange={(e) => setRecoveryEmail(e?.target?.value)}
        />
        {recoveryMessage && (
          <p className={`text-sm ${recoveryMessage?.includes('تم') ? 'text-success' : 'text-error'}`}>
            {recoveryMessage}
          </p>
        )}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowForgotPasswordModal(false)}
            className="flex-1"
          >
            إلغاء
          </Button>
          <Button
            variant="primary"
            onClick={handlePasswordRecovery}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'جاري الإرسال...' : 'إرسال'}
          </Button>
        </div>
      </div>
    </Modal>

    {/* Sign Up Modal */}
    <Modal
      isOpen={showSignUpModal}
      onClose={() => setShowSignUpModal(false)}
      title="إنشاء حساب جديد"
      icon="UserPlus"
      iconColor="var(--color-primary)"
      footer={null}
    >
      <div className="space-y-4">
        <p className="text-sm text-white/70">
          املأ البيانات التالية لإنشاء حساب جديد. سيتم مراجعة طلبك من قبل المسؤول.
        </p>
        
        <Input
          type="text"
          label="الاسم الكامل"
          placeholder="أدخل اسمك الكامل"
          value={signUpData?.fullName}
          onChange={(e) => setSignUpData(prev => ({ ...prev, fullName: e?.target?.value }))}
          error={signUpErrors?.fullName}
        />

        <Input
          type="email"
          label="البريد الإلكتروني"
          placeholder="example@realestate.com"
          value={signUpData?.email}
          onChange={(e) => setSignUpData(prev => ({ ...prev, email: e?.target?.value }))}
          error={signUpErrors?.email}
        />

        <Input
          type="password"
          label="كلمة المرور"
          placeholder="أدخل كلمة المرور (6 أحرف على الأقل)"
          value={signUpData?.password}
          onChange={(e) => setSignUpData(prev => ({ ...prev, password: e?.target?.value }))}
          error={signUpErrors?.password}
        />

        <Input
          type="password"
          label="تأكيد كلمة المرور"
          placeholder="أعد إدخال كلمة المرور"
          value={signUpData?.confirmPassword}
          onChange={(e) => setSignUpData(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
          error={signUpErrors?.confirmPassword}
        />

        <Select
          label="الدور الوظيفي"
          placeholder="اختر دورك الوظيفي"
          options={roleOptions}
          value={signUpData?.role}
          onChange={(value) => setSignUpData(prev => ({ ...prev, role: value }))}
          error={signUpErrors?.role}
        />

        {signUpErrors?.submit && (
          <div className="flex items-start gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{signUpErrors?.submit}</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowSignUpModal(false)}
            className="flex-1"
          >
            إلغاء
          </Button>
          <Button
            variant="primary"
            onClick={handleSignUp}
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? 'جاري الإنشاء...' : 'إنشاء حساب'}
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default LoginForm;